import { useCallback, memo, FC } from 'react';
// import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Edit3, Share2 } from 'lucide-react';

import { useDownloadAppModal } from '@hooks/useDownloadAppModal';
import { useShareModal } from '@hooks/useShareModal';
import { generateProfileShareData } from '@utils/share';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import { DefaultText } from '@components/text';

type Props = {
  userId: string;
};

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
`;

const Button = styled(TouchableOpacity)<{ $variant: 'edit' | 'share' }>`
  min-height: 48px;
  display: flex;
  flex-direction: row;
  position: relative;
  justify-content: center;
  align-items: center;
  padding: 8px 24px;
  background-color: ${({ theme }) =>
    `${theme.colors.black}${theme.opacity[65]}`};
  border-radius: 8px;
  border: 0.5px solid ${({ theme }) => theme.colors.grey[600]};
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ $variant }) =>
    $variant === 'edit' &&
    `
    flex: 1;
  `}

  ${({ $variant }) =>
    $variant === 'share' &&
    `
    width: 56px;
    padding: 8px;
  `}

  &:hover {
    background-color: ${({ theme }) =>
      `${theme.colors.black}${theme.opacity[15]}`};
    border-color: ${({ theme }) => theme.colors.grey[500]};
  }
`;

const ButtonIcon = styled.div<{ $variant: 'edit' | 'share' }>`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $variant }) =>
    $variant === 'edit' &&
    `
    margin-right: 8px;
  `}
`;

const EditButtonText = styled(DefaultText)`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  font-weight: ${({ theme }) => theme.fonts.fontWeight.bold};
`;

const ProfileButtons: FC<Props> = ({ userId }) => {
  //   const navigate = useNavigate();
  const { openDownloadAppModal } = useDownloadAppModal();
  const { openShareModal } = useShareModal();

  const onEditPress = useCallback(() => {
    // TODO: Implement edit functionality
    openDownloadAppModal();
  }, [openDownloadAppModal]);

  const onSharePress = useCallback(() => {
    if (userId) {
      const shareData = generateProfileShareData(userId);
      openShareModal(shareData);
    }
  }, [userId, openShareModal]);

  return (
    <ButtonContainer>
      <Button $variant='edit' onClick={onEditPress}>
        <ButtonIcon $variant='edit'>
          <Edit3 size={24} />
        </ButtonIcon>
        <EditButtonText>Edit profile</EditButtonText>
      </Button>

      <Button $variant='share' onClick={onSharePress}>
        <ButtonIcon $variant='share'>
          <Share2 size={24} />
        </ButtonIcon>
      </Button>
    </ButtonContainer>
  );
};

export default memo(ProfileButtons);
